import { fa, toman, qty } from './fa';
import { shopLabel } from './shopname';
import type { CartLine, SaleResult } from './types';

/*
 * After the money.
 *
 * The screen exists to answer one question fast — is this done, can the next
 * customer come forward — and then to hand the paperwork over. The sync card
 * is deliberately prominent and deliberately honest: a sale made offline is a
 * legal invoice with its tax number already allocated, and the customer can
 * leave. Saying "sent" when it is queued would be a lie the shopkeeper only
 * discovers at filing time.
 */

export function ReceiptScreen({
  sale, lines, shopName, branchName, cashierName, economicCode, customerPhone,
  paidMethodLabel, paidRial, changeRial, queued, onNewSale,
  printState, printError, onPrint,
}: {
  sale: SaleResult;
  lines: CartLine[];
  /**
   * The shop's own name, for the top of the slip a customer keeps.
   *
   * Optional, and falls back: a device that has not replicated its merchant
   * identity cannot name the shop, and a receipt headed with an empty string is
   * worse than one headed with the product.
   */
  shopName?: string;
  branchName: string;
  cashierName: string;
  economicCode: string | null;
  customerPhone: string | null;
  paidMethodLabel: string;
  paidRial: number;
  changeRial: number;
  queued: boolean;
  onNewSale: () => void;
  printState: 'idle' | 'printing' | 'done' | 'failed';
  printError: string | null;
  onPrint: () => void;
}) {
  const issued = new Date(sale.issuedAt);
  const timeLabel = fa(
    `${String(issued.getHours()).padStart(2, '0')}:${String(issued.getMinutes()).padStart(2, '0')}`,
  );

  return (
    <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
      <div style={{ flex: 1, minWidth: 0, padding: 18, display: 'flex', flexDirection: 'column', gap: 14, overflow: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div
            style={{
              width: 44, height: 44, borderRadius: 12, background: 'var(--luma-tint-success-bg)',
              border: '1px solid var(--luma-tint-success-border)', display: 'grid', placeItems: 'center',
              color: 'var(--luma-tint-success-text)', fontSize: 20, fontWeight: 800,
            }}
          >
            ✓
          </div>
          <div>
            <div style={{ fontWeight: 800, fontSize: 24 }}>فروش ثبت شد</div>
            <div style={{ fontSize: 13.5, color: 'var(--pos-ink-3)' }}>
              {paidMethodLabel} · {toman(sale.totalRial)} · ساعت {timeLabel}
            </div>
          </div>
          <div style={{ flex: 1 }} />
          <button
            onClick={onNewSale}
            autoFocus
            style={{
              height: 56, padding: '0 22px', borderRadius: 12, background: 'var(--luma-accent)',
              color: '#FFF', fontWeight: 800, fontSize: 16,
            }}
          >
            فروش بعدی
          </button>
        </div>

        <div
          style={{
            borderRadius: 12, padding: '14px 16px',
            border: `1px solid ${queued ? 'var(--luma-tint-warning-border)' : 'var(--luma-tint-success-border)'}`,
            background: queued ? 'var(--luma-tint-warning-bg)' : 'var(--luma-tint-success-bg)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                width: 10, height: 10, borderRadius: '50%',
                background: queued ? 'var(--luma-accent)' : 'var(--luma-success)',
                animation: queued ? 'lumaPulse 1.6s ease-in-out infinite' : undefined,
              }}
            />
            <div style={{ fontWeight: 800, fontSize: 16 }}>
              {queued ? 'در انتظار همگام‌سازی با سرور' : 'ثبت شد'}
            </div>
            <div style={{ flex: 1 }} />
            <div className="pos-num" style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--luma-ink-2)' }}>
              {sale.number}
            </div>
          </div>
          <div style={{ fontSize: 13, color: 'var(--luma-ink-2)', marginTop: 4, lineHeight: 1.6, maxWidth: '88ch' }}>
            {/*
              Honest about what has and has not happened. The invoice is legal
              and numbered either way; transmission is a separate step, and a
              shop that thinks a queued invoice is filed finds out at quarter end.
            */}
            {queued
              ? 'فاکتور صادر و شماره‌گذاری شد؛ همگام‌سازی با سرور به‌محض وصل شدن اینترنت انجام می‌شود. مشتری می‌تواند با همین رسید برود.'
              : 'فاکتور در سیستم ثبت شد.'}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 12 }}>
          <div style={{ background: 'var(--luma-card)', border: '1px solid var(--luma-line)', borderRadius: 12, padding: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em', color: 'var(--luma-placeholder)' }}>
              تحویل فاکتور به مشتری
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <div
                className="pos-num"
                style={{
                  flex: 1, height: 52, border: '1px solid var(--luma-line-strong)', borderRadius: 10,
                  background: 'var(--luma-sunken)', display: 'flex', alignItems: 'center',
                  padding: '0 14px', fontWeight: 700, fontSize: 17,
                  color: customerPhone ? undefined : 'var(--luma-placeholder)',
                }}
              >
                {customerPhone ? fa(customerPhone) : 'شماره‌ای ثبت نشده'}
              </div>
            </div>
            {/*
              * The state of the actual paper, said plainly.
              *
              * This used to be a sentence saying printing was not built yet,
              * while the payment button said «و چاپ فاکتور». One of the two was
              * lying, and it was the button — so it now prints, and this says
              * what happened to the paper rather than what the feature is.
              */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
              <button
                onClick={onPrint}
                disabled={printState === 'printing'}
                style={{
                  height: 46, padding: '0 18px', borderRadius: 10, fontWeight: 700, fontSize: 15,
                  cursor: printState === 'printing' ? 'default' : 'pointer',
                  border: '1px solid var(--luma-line-strong)', background: 'var(--luma-card)',
                  opacity: printState === 'printing' ? 0.6 : 1,
                }}
              >
                {printState === 'printing' ? 'در حال چاپ…'
                  : printState === 'done' ? 'چاپ دوباره'
                  : 'چاپ فاکتور'}
              </button>
              <div style={{ fontSize: 13, fontWeight: 700, color: printState === 'failed' ? 'var(--luma-danger, #A8352C)' : 'var(--luma-ink-2)' }}>
                {printState === 'done' ? 'فاکتور چاپ شد'
                  : printState === 'failed' ? (printError ?? 'چاپ فاکتور انجام نشد')
                  : printState === 'printing' ? '' : ''}
              </div>
            </div>
          </div>

          <div
            style={{
              background: 'var(--luma-card)', border: '1px solid var(--luma-line)',
              borderRadius: 12, padding: 14, display: 'flex', gap: 14, alignItems: 'center',
            }}
          >
            <div
              style={{
                width: 104, height: 104, flex: 'none', border: '1px dashed var(--pos-rule-frame)',
                borderRadius: 10, background: 'var(--luma-sunken)', display: 'grid', placeItems: 'center',
                textAlign: 'center', fontSize: 11, color: 'var(--luma-placeholder)', lineHeight: 1.4, padding: 6,
              }}
            >
              QR فاکتور الکترونیکی
            </div>
            <div style={{ lineHeight: 1.55 }}>
              <div style={{ fontWeight: 800, fontSize: 15 }}>اسکن برای فاکتور الکترونیکی</div>
              <div style={{ fontSize: 12.5, color: 'var(--pos-ink-3)' }}>
                لینک فاکتور رسمی پس از راه‌اندازی سرویس فاکتور الکترونیکی اینجا تولید می‌شود.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* --- the receipt itself -------------------------------------------- */}
      <div
        style={{
          width: 428, flex: 'none', background: 'var(--pos-receipt-ground)',
          borderInlineStart: '1px solid var(--luma-line)', padding: 18,
          overflow: 'auto', display: 'flex', justifyContent: 'center',
        }}
      >
        <div
          style={{
            width: 360, background: '#FFF', padding: '20px 22px 26px',
            boxShadow: '0 3px 10px rgba(32,30,29,.16)', fontSize: 12.5, lineHeight: 1.75,
          }}
        >
          <div style={{ textAlign: 'center', paddingBottom: 10, borderBottom: '1px dashed var(--pos-rule-frame)' }}>
            {/*
              * The shop's name, not the vendor's.
              *
              * This read "فروشگاه لوما" on every slip in every shop — the name
              * of the software, printed at the top of a document the customer
              * takes away and may bring back to ask about. A receipt that names
              * the wrong business is worse than one that names none: it is the
              * record of who took the money.
              */}
            <div style={{ fontWeight: 800, fontSize: 17 }}>
              {shopLabel(shopName, 'فروشگاه')} — {branchName}
            </div>
            {economicCode && (
              <div className="pos-num" style={{ color: 'var(--luma-ink-2)' }}>
                شناسه اقتصادی {fa(economicCode)}
              </div>
            )}
          </div>

          <div
            className="pos-num"
            style={{
              display: 'flex', justifyContent: 'space-between', padding: '8px 0',
              borderBottom: '1px dashed var(--pos-rule-frame)',
            }}
          >
            <span>{sale.number}</span>
            <span>{timeLabel}</span>
          </div>

          {lines.map((l) => (
            <div key={l.item.variantId} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '5px 0' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700 }}>{l.item.name}</div>
                <div className="pos-num" style={{ color: 'var(--luma-ink-2)', fontSize: 11.5 }}>
                  {toman(l.item.priceRial)} × {qty(l.quantity)}
                </div>
              </div>
              <div className="pos-num" style={{ fontWeight: 700 }}>
                {toman(Math.round(l.item.priceRial * l.quantity) - l.discountRial)}
              </div>
            </div>
          ))}

          <div style={{ borderTop: '1px dashed var(--pos-rule-frame)', marginTop: 8, paddingTop: 8 }}>
            <Line k="جمع" v={toman(sale.subtotalRial)} />
            <Line k="تخفیف" v={toman(sale.discountRial)} />
            <Line k="مالیات ارزش افزوده" v={toman(sale.vatRial)} />
            <div
              style={{
                display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: 16,
                marginTop: 6, paddingTop: 6, borderTop: '1px solid var(--luma-ink)',
              }}
            >
              <span>قابل پرداخت</span>
              <span className="pos-num">{toman(sale.totalRial)}</span>
            </div>
            <Line k={paidMethodLabel} v={toman(paidRial)} />
            {changeRial > 0 && <Line k="باقی‌مانده" v={toman(changeRial)} />}
          </div>

          <div
            style={{
              textAlign: 'center', color: 'var(--luma-ink-2)', marginTop: 10,
              borderTop: '1px dashed var(--pos-rule-frame)', paddingTop: 10,
            }}
          >
            صندوق‌دار: {cashierName} · متشکریم 🙂
          </div>
        </div>
      </div>
    </div>
  );
}

function Line({ k, v }: { k: string; v: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <span>{k}</span>
      <span className="pos-num">{v}</span>
    </div>
  );
}
